import { useData, useDataAuth } from "../../../../../runtime/provider";
import { randomUUID } from "expo-crypto";
import React, { useState, useEffect } from "react";
import { ScrollView, Platform } from "react-native";
import { useQuery, useMutation } from "@tanstack/react-query";
import LBox from "@components/LBox";
import LText from "@components/LText";
import LIcon from "@components/LIcon";
import LPressable from "@components/LPressable";
import { usePayment } from "@utils/stripe/usePayment";
import { useAuthSheet } from "@components/modals/AuthModal/useAuthSheet";
import { limitRefusalMessage } from "@libs/checkout-core";
import {
  clampQuantity,
  detailsReady,
  linesFrom,
  openingQuantity,
  orderLimitState,
  tierBuyable,
  tierMax,
} from "@libs/shared-ui";
import { EventTicketsScreen } from "@libs/shared-ui/native";
import useAccessCreateCheckout from "@hooks/trpc/useAccessCreateCheckout";
import type { EngineCheckoutSession } from "@components/Checkout/engineSession";
import Close from "../common/Close";

/** Engine events: tier loading, selection, waitlist and the checkout,
 *  shared by the Select tickets sheet (Details → Get tickets) and the
 *  full-screen /tickets and /checkout routes (deep links).
 *
 *  The sheet (EngineTicketsSheet) holds the tickets with `hold` and runs
 *  the checkout on the engine session it hands back, in the same sheet
 *  (LIS-975): native on iOS and Android, the embedded web checkout on
 *  app-web. The full-screen routes open the engine's hosted checkout
 *  (`checkout`), as before. */
export function useEngineCheckout(
  accessId: string,
  {
    funnelId,
  }: {
    /** The purchase funnel the selection is made in (LIS-1110): the hold
     *  carries it to the checkout it opens. */
    funnelId?: string;
  } = {},
) {
  const data = useData();
  // The tiers, with the event's per-order limits across all of them.
  const tickets = useQuery({
    queryKey: ["engine-tickets", accessId],
    queryFn: () => data.api.accesses.getEngineTickets.query(accessId),
    refetchInterval: 15000,
  });
  // What the event's organiser asks of a fan beyond their email (LIS-1000).
  const rules = useQuery({
    queryKey: ["engine-buyer-rules", accessId],
    queryFn: () => data.api.accesses.getEngineBuyerRules.query(accessId),
  });
  const buyerRules = rules.data ?? { name: "off", phone: "off" };
  const [buyerName, setBuyerName] = useState("");
  const [buyerPhone, setBuyerPhone] = useState("");
  const buyer = {
    name: buyerRules.name === "off" ? "" : buyerName.trim(),
    phone: buyerRules.phone === "off" ? "" : buyerPhone.trim(),
  };
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [touched, setTouched] = useState(false);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const { firebaseUser } = useDataAuth();
  const signIn = useAuthSheet();
  const active = (tickets.data?.tiers ?? []).filter((t) => t.active);
  const orderLimits = tickets.data?.orderLimits ?? null;
  // One ticket of the first type on sale (as many as the event's minimum
  // per order, within the type's own limit) until the buyer changes
  // anything, as on the web: Continue is ready the moment the sheet opens.
  const first = active.find((t) => tierBuyable(t, now));
  const opening = openingQuantity(orderLimits, { maxQuantity: tierMax(first) });
  const effective =
    !touched && first && opening
      ? { [first.engineItemId]: opening }
      : quantities;
  const lines = linesFrom(active, effective, now);
  // The event's per-order limits, across all its tiers (LIS-924).
  const limits = orderLimitState(orderLimits, lines);
  const createCheckout = useAccessCreateCheckout();
  const payment = usePayment(
    accessId,
    lines.reduce((n, l) => n + l.quantity, 0),
    undefined,
    false,
    undefined,
    undefined,
    lines,
    buyer,
  );
  const waitlist = useMutation({
    mutationFn: (input: {
      itemId: string;
      idempotencyKey: string;
      name: string;
      phone?: string;
      quantity?: number;
    }) =>
      data.api.accesses.joinWaitlist.mutate({
        accessId,
        ...input,
      }),
    onSuccess: (_joined, input) =>
      setMessage(
        active.find((t) => t.engineItemId === input.itemId)?.requiresApproval
          ? "Request sent. We'll email you when the organiser decides."
          : "You're on the waitlist. Watch your email for an invitation.",
      ),
    onError: () => setMessage("Could not join the waitlist. Please try again."),
  });
  async function checkout() {
    if (!firebaseUser && Platform.OS !== "web") {
      signIn();
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      await payment.openPaymentSheet();
    } catch {
      setMessage("Could not open checkout. Please try again.");
    } finally {
      setBusy(false);
      await tickets.refetch();
    }
  }
  /** Holds the selection in an engine session for the sheet's checkout:
   *  the session, or null with why said over the tickets. */
  async function hold(): Promise<EngineCheckoutSession | null> {
    setMessage("");
    const requestedAt = Date.now();
    try {
      const response = await createCheckout.mutateAsync({
        accessId,
        lineItems: lines,
        numberOfTickets: lines.reduce((n, l) => n + l.quantity, 0),
        ...(buyer.name ? { name: buyer.name } : {}),
        ...(buyer.phone ? { phone: buyer.phone } : {}),
        ...(funnelId ? { funnelId } : {}),
        purchasePlatform:
          Platform.OS === "ios"
            ? "ios"
            : Platform.OS === "android"
              ? "android"
              : "app-web",
      });
      if (!response || !("kind" in response) || response.kind !== "engine")
        throw new Error("not_an_engine_checkout");
      return {
        accessId,
        url: response.url,
        clientSecret: response.clientSecret,
        apiBaseUrl: response.apiBaseUrl,
        timing: { requestedAt, sessionAt: Date.now() },
      };
    } catch (error) {
      // A limit the engine still refuses (the per-customer cap, which the
      // sheet cannot know) in the checkout's plain words; anything else as
      // before.
      setMessage(
        limitRefusalMessage(refusalOf(error)) ??
          "We couldn't hold those tickets. Availability may have changed — try again.",
      );
      await tickets.refetch();
      return null;
    }
  }
  const setQuantity = (id: string, next: number) => {
    // The first change starts from what the buyer saw, the default included.
    setQuantities((q) => {
      const from = touched ? q : effective;
      // More than the event allows in one order, counted across its tiers:
      // the increase does not happen.
      const { room } = orderLimitState(
        orderLimits,
        linesFrom(active, from, now),
      );
      if (next - (from[id] ?? 0) > room) return from;
      // The organiser's per-order limit, where the tier has one.
      return {
        ...from,
        [id]: clampQuantity(
          next,
          0,
          tierMax(active.find((t) => t.engineItemId === id)),
        ),
      };
    });
    setTouched(true);
  };
  const joinWaitlist = (
    itemId: string,
    name: string,
    request?: { phone?: string; quantity?: number },
  ) =>
    firebaseUser
      ? waitlist.mutate({
          itemId,
          name,
          ...request,
          idempotencyKey: randomUUID(),
        })
      : signIn();
  return {
    tiers: tickets,
    active,
    now,
    quantities: effective,
    lines,
    orderLimits,
    /** Why the selection cannot continue (below the event's minimum), or
     *  null; the ticket screen shows it as a hint. */
    limitProblem: limits.problem,
    busy,
    waitlistPending: waitlist.isPending,
    message,
    /** Say something over the tickets (the hold ran out, say). */
    showMessage: setMessage,
    setQuantity,
    joinWaitlist,
    checkout,
    hold,
    /** The event's buyer fields for the tickets screen, and whether the
     *  selection may continue with what has been typed. */
    buyerDetails: {
      rules: buyerRules,
      name: buyerName,
      phone: buyerPhone,
      onName: setBuyerName,
      onPhone: setBuyerPhone,
    },
    detailsReady: detailsReady(buyerRules, {
      name: buyerName,
      phone: buyerPhone,
    }),
  };
}

/** The engine's refusal as the API hands it on (`data.reason` and
 *  `data.detail` on the tRPC error), in the envelope checkout-core reads. */
const refusalOf = (error: unknown) => {
  const data = (error as { data?: { reason?: unknown; detail?: unknown } })
    ?.data;
  return { error: { code: data?.reason, detail: data?.detail } };
};

/** Full-screen Select tickets for the /tickets and /checkout routes. */
export default function EngineCheckout({
  accessId,
  onClose,
}: {
  accessId: string;
  onClose?: () => void;
}) {
  const {
    tiers,
    active,
    now,
    quantities,
    lines,
    orderLimits,
    busy,
    waitlistPending,
    message,
    setQuantity,
    joinWaitlist,
    checkout,
    buyerDetails,
  } = useEngineCheckout(accessId);
  const back = onClose;
  return (
    <ScrollView
      contentContainerStyle={{ padding: 20, paddingTop: 12, flexGrow: 1 }}
    >
      <LBox
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        mb="s4"
      >
        {back ? (
          <LPressable
            onPress={back}
            sentryEventName="EngineCheckout.Back"
            hitSlop={8}
          >
            <LIcon
              name="chevron"
              rotate={90}
              size={18}
              colors={{ color: "white" }}
            />
          </LPressable>
        ) : (
          <LBox width={18} />
        )}
        <LText variant="Heading/H2/SemiBold" textAlign="center">
          Select tickets
        </LText>
        {onClose ? (
          <Close onPress={onClose} sentryEventName="EngineCheckout.Close" />
        ) : (
          <LBox width={18} />
        )}
      </LBox>
      {tiers.isLoading ? <LText>Loading tickets…</LText> : null}
      {tiers.isError ? (
        <LPressable
          onPress={() => void tiers.refetch()}
          sentryEventName="engine-tiers-retry"
        >
          <LText>Retry loading tickets</LText>
        </LPressable>
      ) : null}
      <EventTicketsScreen
        tiers={active}
        now={now}
        quantities={quantities}
        lines={lines}
        orderLimits={orderLimits}
        busy={busy || waitlistPending}
        message={message}
        waitlistNeedsEmail={false}
        onQuantity={setQuantity}
        onWaitlist={(itemId, name, _email, request) =>
          joinWaitlist(itemId, name, request)
        }
        buyerDetails={buyerDetails}
        onContinue={() => void checkout()}
        continueLabel={busy ? "Opening checkout…" : "Continue to checkout"}
      />
    </ScrollView>
  );
}
